// src/pages/TechniqueSection.jsx
//
// Student-facing render of a skills-workshop `technique` lit_section. Mirrors the
// Lit*Section components (pure presentational, takes `content` directly, neutral
// book-* palette) so it drops into the shared grouped reader the same way.
//
// TWO content models (technique-flexible-subheadings):
//   NEW  -- blocks.parts: an author-managed array of { id, heading, body,
//           image:{imageUrl,caption,alt}, video } subheadings (any number, ordered).
//           Renders keyIdea -> each part (subheading -> prose -> image -> video) ->
//           steps -> examples.
//   LEGACY -- no (renderable) parts: the original fixed blocks
//           { explanation, whenToUse, image, video } render EXACTLY as before, so
//           never-migrated sections are byte-identical. The editor migrates lazily
//           on first edit (see TechniqueEditor) and clears the legacy keys.
// keyIdea / steps / examples stay dedicated blocks in BOTH models. The unit
// grouping (blocks.unit) is nav metadata and is NOT rendered here.
import React, { useState, useRef, useEffect } from 'react';
import AnnotatedText from '../components/AnnotatedText.jsx';
import RichText from '../components/richText/RichText.jsx';
import WorkedExampleReveal from '../components/WorkedExampleReveal.jsx';
import YouTubeEmbed, { parseYouTubeId } from '../components/YouTubeEmbed.jsx';
import { freePlaybackUrl } from '../components/audioUrl.js';
import { imageFrameStyle, imageFrameBox } from '../utils/imageFrame.js';
import { selectRenderableParts, partImages, partStarters, normalizePartStyle } from './techniqueParts.js';
import LessonCard, { LessonCardHeading, ReadMore } from '../components/reader/LessonCard.jsx';
import LessonHeader from '../components/reader/LessonHeader.jsx';
import SentenceMap from '../components/sentenceMap/SentenceMap.jsx';
import PatternCards from '../components/sentenceMap/PatternCards.jsx';
import { patternCardView } from '../components/sentenceMap/patternCards.js';
import { practiceView } from '../components/practiceActivities/model.js';
import PracticeActivities, { OriginalExercise } from '../components/practiceActivities/PracticeActivities.jsx';
import LessonPartExtras from '../components/lessonExtras/LessonPartExtras.jsx';
import BoundLessonMedia from '../components/lessonExtras/BoundLessonMedia.jsx';

function EmptyNote() {
    return <p className="font-serif italic text-book-muted text-sm">Nothing here yet.</p>;
}

// Highlighted one-line takeaway (keyIdea) -- shared with AnalysisSection. Bordered
// book-accent box in the StartHereCard visual language; renders nothing when blank.
export function KeyIdeaBox({ keyIdea }) {
    if (!keyIdea) return null;
    // lesson-3a: sits directly under the lesson header card. Same accent callout,
    // now on tokens with no decorative corner.
    return (
        <div className="mb-6 rounded-[var(--radius-card)] border border-book-accent/40 bg-book-accent/5 p-4">
            <div className="font-display text-book-accent text-xs mb-1.5 font-semibold">Key idea</div>
            <p className="font-accent italic text-book-text text-base md:text-lg leading-relaxed">
                <AnnotatedText>{keyIdea}</AnnotatedText>
            </p>
        </div>
    );
}

// Optional image stimulus ({ imageUrl, caption?, alt? }). Free-scope uploads persist
// the direct storage.googleapis.com URL (IAM-governed, org policy forbids public
// IAM) -- freePlaybackUrl maps it to the rules-governed firebasestorage alt=media
// form (public/samples/** is public-read), exactly as free audio does; external /
// already-mapped URLs pass through unchanged. A broken URL hides the whole figure
// (onError -> state) so no torn image or orphaned caption ever renders.
//
// authoring-5a-blocks: the figure is now a button that opens a lightbox. The
// wrapping button is styled inert (no padding, border or background) and the <img>
// keeps its EXACT classes, so a never-edited lesson has zero pixel delta -- it only
// gains a keyboard-focusable way to enlarge the picture.
export function StimulusImage({ image, className = 'mb-7' }) {
    const [failed, setFailed] = useState(false);
    const [open, setOpen] = useState(false);
    const url = image && typeof image === 'object' && typeof image.imageUrl === 'string' ? image.imageUrl.trim() : '';
    if (!url || failed) return null;
    const alt = (image.alt && String(image.alt)) || '';
    const caption = (typeof image.caption === 'string' && image.caption) ? image.caption : '';
    const src = freePlaybackUrl(url);
    return (
        <figure className={className}>
            <button type="button" onClick={() => setOpen(true)}
                aria-label={'Enlarge image' + (alt ? ': ' + alt : '')}
                style={image.frame?.enabled ? imageFrameBox(image.frame) : undefined}
                className={'block w-full p-0 m-0 border-0 bg-transparent text-left cursor-zoom-in rounded-none' + (image.frame?.enabled ? ' overflow-hidden' : '')}>
                <img src={src} alt={alt}
                    style={image.frame?.enabled ? imageFrameStyle(image.frame) : undefined}
                    onError={() => setFailed(true)}
                    className={'w-full max-w-full border border-book-accent/30 bg-book-pageDark' + (image.frame?.enabled ? ' h-full object-cover' : '')} loading="lazy" />
            </button>
            {caption && (
                <figcaption className="font-serif italic text-book-muted text-xs mt-2">{caption}</figcaption>
            )}
            {open && <ImageLightbox src={src} alt={alt} caption={caption} onClose={() => setOpen(false)} />}
        </figure>
    );
}

// A minimal accessible lightbox on the native <dialog>.
//
// REACT OWNS `open`. The first cut let the native 'close' event be the only bridge
// back into React state; a dialog closed natively then sat mounted-but-closed and
// the next open was a no-op state update. So every close path now goes THROUGH
// React: Esc arrives as the cancelable 'cancel' event -> preventDefault (the DOM
// never closes on its own) -> onClose(); the backdrop and the Close button call
// onClose() directly. The platform still does the accessibility work -- showModal()
// gives the top layer and traps focus, and the unmount cleanup calls close() so the
// browser RETURNS FOCUS to the element that opened it. A backdrop click lands on
// the dialog element itself (target === currentTarget); clicks inside do not.
// Rendered in-tree, not portalled, so the book-* tokens inherit the live theme.
function ImageLightbox({ src, alt, caption, onClose }) {
    const ref = useRef(null);
    const onCloseRef = useRef(onClose);
    useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
    useEffect(() => {
        const d = ref.current;
        if (!d) return undefined;
        // The opener is whatever had focus at mount -- the button that was just
        // clicked. Remembered here because the platform's own focus return runs in
        // the dialog's close steps, which need the element still in the document;
        // by the time this cleanup runs the dialog is leaving the DOM in the same
        // commit, so focus would land on <body>. Returning it ourselves is
        // deterministic.
        const opener = document.activeElement;
        if (typeof d.showModal === 'function' && !d.open) d.showModal();
        const onCancel = (e) => { e.preventDefault(); onCloseRef.current(); };
        // Belt and braces: any OTHER native close (e.g. a UA gesture) still syncs.
        const onNativeClose = () => onCloseRef.current();
        d.addEventListener('cancel', onCancel);
        d.addEventListener('close', onNativeClose);
        return () => {
            d.removeEventListener('cancel', onCancel);
            d.removeEventListener('close', onNativeClose);
            if (d.open) d.close(); // clears the top layer
            if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
        };
    }, []);
    return (
        <dialog ref={ref}
            className="p-0 m-auto bg-transparent max-w-[min(96vw,1100px)] outline-none backdrop:bg-black/75"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            aria-label={alt || 'Enlarged image'}>
            <div className="rounded-[var(--radius-card)] bg-book-page border border-book-line shadow-[var(--shadow-card)] p-2">
                <img src={src} alt={alt} className="block max-h-[85vh] w-auto max-w-full mx-auto" />
                <div className="flex items-center justify-between gap-3 px-2 pt-2 pb-1">
                    <span className="font-serif italic text-book-muted text-xs min-w-0 truncate">{caption}</span>
                    <button type="button" onClick={onClose}
                        className="flex-shrink-0 font-display text-book-accent text-xs font-semibold px-2 py-1 hover:underline">
                        Close
                    </button>
                </div>
            </div>
        </dialog>
    );
}

// authoring-5a-blocks: a part's images. One -> full column width, exactly as the
// single image always rendered. Two -> two equal columns. Three or more -> a grid,
// two columns on a phone and three from md. Each figure keeps its own caption and
// alt, and a broken URL hides ONLY that figure (StimulusImage's onError) -- never
// the grid.
export function PartImages({ images }) {
    if (!images || images.length === 0) return null;
    if (images.length === 1) return <StimulusImage image={images[0]} />;
    const cols = images.length === 2 ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-3';
    return (
        <div className={'grid gap-3 ' + cols}>
            {images.map((im, i) => <StimulusImage key={im.id || 'img' + i} image={im} className="mb-0" />)}
        </div>
    );
}

// authoring-5a-blocks: the four SEMANTIC card styles. Every colour is a token, so
// the three themes follow; there is no free colour for an author to choose. The
// label sits in normal ink -- accent text on its own tint measured under AA in the
// 3b/3c work, so the colour signal here is the tint plus the left rule, not the text.
const STYLE_META = {
    normal:  { label: '', bg: '', rule: '' },
    tip:     { label: 'Tip',       bg: 'rgb(var(--accent-soft))', rule: 'rgb(var(--accent))' },
    warning: { label: 'Watch out', bg: 'rgb(var(--warn) / 0.10)', rule: 'rgb(var(--warn))' },
    example: { label: 'Example',   bg: 'rgb(var(--bg-deep))',     rule: 'rgb(var(--library))' },
};

// One flexible subheading part: heading, body prose, then (5a) the sentence
// starters, then its images and its click-to-load video. Media gates mirror the
// actual render conditions so degenerate data leaves no gap. Exported for the
// analysis / vocabulary / guide renderers.
//
// BACK-COMPAT (the 5a invariant): a never-edited part has no `images`, no
// `starters` and no `style`, so partImages() returns its legacy image as one figure,
// partStarters() is empty, the style is 'normal', and every wrapper class below
// evaluates exactly as it did before -- byte-identical markup apart from the inert
// lightbox button around the image.
export function TechniquePart({ part, practice, missionContent, lessonOptions }) {
    const heading = typeof part.heading === 'string' ? part.heading.trim() : '';
    const body = typeof part.body === 'string' ? part.body : '';
    const images = partImages(part);
    const starters = partStarters(part);
    const video = typeof part.video === 'string' ? part.video : '';
    const videoRenderable = !!parseYouTubeId(video);
    const style = normalizePartStyle(part.style);
    const meta = STYLE_META[style];
    const styled = style !== 'normal';
    // Inline token styles: they beat LessonCard's own bg-book-page without a
    // specificity fight, and they are still tokens -- never hex.
    const cardStyle = styled ? { background: meta.bg, borderLeft: '3px solid ' + meta.rule } : undefined;
    const hasProse = !!(heading || body);
    // lesson-3a: every part is a card. A long body auto-collapses behind "Read more"
    // (decided from the text, so it is the same at every reading scale) -- a wall of
    // prose on a 390px screen is the thing students bounce off.
    return (
        <LessonCard className="mb-4" style={cardStyle} data-part-style={styled ? style : undefined}>
            {styled && (
                <div className="font-display text-book-text text-xs font-semibold mb-1.5">{meta.label}</div>
            )}
            {/* On a tinted card the heading uses normal ink: accent-on-tint measured
                4.45:1 on the warn tint in Dark. The tint and the rule carry the style. */}
            {heading && (styled
                ? <div className="font-display text-book-text text-xs mb-2 font-semibold">{heading}</div>
                : <LessonCardHeading>{heading}</LessonCardHeading>)}
            <LessonPartExtras content={missionContent} partId={part.id} options={lessonOptions} />
            {practice?.activities.some(a => a.partId === part.id) ? <>
                <PracticeActivities view={practice} partId={part.id} renderMedia={missionContent?.blocks?.practice?.media?.length ? settings => <BoundLessonMedia content={missionContent} partId={part.id} options={lessonOptions} {...settings} /> : undefined} />
                <OriginalExercise body={body} />
            </> : body && patternCardView(body, part.patternCards) ? <PatternCards body={body} value={part.patternCards} /> : body && (
                <ReadMore text={body}>
                    {/* 5b: markdown body. Same classes the <p> carried, so an unmarked body is unchanged. */}
                    <RichText text={body} className="font-serif text-book-text text-base leading-relaxed" />
                </ReadMore>
            )}
            {/* Sentence starters: numbered automatically; the template stands on its
                own line on a soft accent band, the filled-in example beneath it,
                indented, with an arrow that wraps WITH the example (flex + min-w-0),
                so a long example on a 390px screen never separates from its glyph. */}
            {starters.length > 0 && (
                <ol className={'space-y-2 ' + (hasProse ? 'mt-4' : '')}>
                    {starters.map((r, i) => {
                        const starter = typeof r.starter === 'string' ? r.starter.trim() : '';
                        const example = typeof r.example === 'string' ? r.example.trim() : '';
                        return (
                            <li key={r.id || 'row' + i} className="rounded-[var(--radius-card)] pl-3 pr-3 py-2"
                                style={{ background: 'rgb(var(--accent-soft))', borderLeft: '3px solid rgb(var(--accent))' }}>
                                {/* Number and example in normal ink: muted-on-tint measured
                                    4.41:1 in Minimalist. Italic serif vs display semibold
                                    still gives the two lines their hierarchy. */}
                                <div className="flex gap-2">
                                    <span className="font-display text-book-text text-xs font-semibold flex-shrink-0 mt-0.5 tabular-nums">{i + 1}.</span>
                                    <div className="min-w-0 flex-grow">
                                        {starter && (
                                            <div className="font-display text-book-text text-sm font-semibold leading-snug [overflow-wrap:anywhere]">{starter}</div>
                                        )}
                                        {example && (
                                            <div className={'font-serif italic text-book-text text-sm leading-relaxed flex gap-1.5 ' + (starter ? 'mt-1' : '')}>
                                                <span aria-hidden="true" className="flex-shrink-0">&rarr;</span>
                                                <span className="min-w-0 [overflow-wrap:anywhere]">{example}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ol>
            )}
            <LessonPartExtras content={missionContent} partId={part.id} placement="after" options={lessonOptions} />
            <SentenceMap value={part.sentenceMap} />
            {images.length > 0 && (
                <div className={hasProse || starters.length ? 'mt-4' : ''}>
                    <PartImages images={images} />
                </div>
            )}
            {videoRenderable && (
                <div className={hasProse || starters.length || images.length ? 'mt-4' : ''}>
                    <YouTubeEmbed url={video} title={(heading || 'Technique') + ' — video'} />
                </div>
            )}
        </LessonCard>
    );
}

export default function TechniqueSection({ content, done = false, lessonOptions }) {
    const blocks = (content && content.blocks) || {};
    const keyIdea = typeof blocks.keyIdea === 'string' ? blocks.keyIdea : '';
    const steps = Array.isArray(blocks.steps) ? blocks.steps.filter((s) => typeof s === 'string' && s.trim()) : [];
    const examples = Array.isArray(blocks.examples)
        ? blocks.examples.filter((e) => e && (e.prompt || e.model)) : [];

    // NEW model: renderable flexible parts (empty array -> the legacy branch below).
    const parts = selectRenderableParts(blocks, parseYouTubeId);
    const practice = practiceView(content);

    // LEGACY model (never-migrated sections): the original fixed blocks.
    const explanation = typeof blocks.explanation === 'string' ? blocks.explanation : '';
    const whenToUse = typeof blocks.whenToUse === 'string' ? blocks.whenToUse : '';
    const image = (blocks.image && typeof blocks.image === 'object' && !Array.isArray(blocks.image)) ? blocks.image : null;
    const video = typeof blocks.video === 'string' ? blocks.video : '';
    // Presence flags MUST mirror the actual render conditions: a caption-only image
    // (imageUrl blank -- the sanitiser keeps the object) or an unparseable video URL
    // renders NOTHING, so it must neither suppress the "Nothing here yet." note nor
    // leave an empty spacer where the embed would be.
    const imageRenderable = !!(image && typeof image.imageUrl === 'string' && image.imageUrl.trim());
    const videoRenderable = !!parseYouTubeId(video);

    const empty = !keyIdea && parts.length === 0 && !explanation && !whenToUse
        && !imageRenderable && !videoRenderable && !steps.length && !examples.length;

    // max-w-2xl caps the reading measure (~66-70ch at this serif size); heading,
    // prose, lists and worked-example cards all share the one capped column.
    return (
        <div className="max-w-2xl mx-auto pb-8">
            <LessonHeader
                eyebrow={typeof blocks.unit === 'string' && blocks.unit.trim() ? blocks.unit.trim() : 'Technique'}
                title={(content && content.title) || 'Technique'} blocks={blocks} done={done} />

            {empty && <EmptyNote />}

            <KeyIdeaBox keyIdea={keyIdea} />

            {parts.length > 0 ? (
                // NEW model: the author's ordered subheadings, each with its media.
                // Key = the stable part id (never the index); id-less hand-authored
                // rows fall back to a positional key for React only.
                parts.map((p, i) => <TechniquePart key={(typeof p.id === 'string' && p.id) || 'part' + i} part={p} practice={practice} missionContent={content} lessonOptions={lessonOptions} />)
            ) : (<>
                {explanation && (
                    <LessonCard className="mb-4">
                        <LessonCardHeading>What it is</LessonCardHeading>
                        <ReadMore text={explanation}>
                            {/* 5b: the LEGACY fields go through the same renderer as parts[].body --
                                only 11 live sections use parts; the other ~97 must not look different. */}
                            <RichText text={explanation} className="font-serif text-book-text text-base leading-relaxed" />
                        </ReadMore>
                    </LessonCard>
                )}

                {whenToUse && (
                    <LessonCard className="mb-4">
                        <LessonCardHeading>When to use it</LessonCardHeading>
                        <ReadMore text={whenToUse}>
                            <RichText text={whenToUse} className="font-serif text-book-text leading-relaxed" />
                        </ReadMore>
                    </LessonCard>
                )}

                {imageRenderable && <StimulusImage image={image} />}

                {videoRenderable && (
                    <div className="mb-7">
                        {/* Click-to-load (no first-paint cost). Gated on parseYouTubeId so an
                            unparseable URL leaves no empty spacer div. */}
                        <YouTubeEmbed url={video} title={((content && content.title) || 'Technique') + ' — video'} />
                    </div>
                )}
            </>)}

            {steps.length > 0 && (
                <div className="mb-4">
                    <LessonCardHeading>How to do it</LessonCardHeading>
                    <ol className="space-y-2">
                        {steps.map((s, i) => (
                            <LessonCard as="li" key={i} className="font-serif text-book-text leading-relaxed flex gap-3">
                                <span className="font-display text-book-accent flex-shrink-0 font-bold">{i + 1}.</span>
                                <RichText text={s} />
                            </LessonCard>
                        ))}
                    </ol>
                </div>
            )}

            {examples.length > 0 && (
                <div className="mb-4">
                    <LessonCardHeading>Worked examples</LessonCardHeading>
                    <div className="space-y-3">
                        {examples.map((e, i) => (
                            <WorkedExampleReveal key={i} prompt={e.prompt} model={e.model} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
